import { isNil } from 'lodash';
import {
  CohortSampleContextType,
  IBuilder,
  IBuilderExpandEvent,
  IBuilderFoldEvent,
  IBuilderInputPayload,
  IBuilderMergeEvent,
  IErrors,
  IRow,
  RowType,
} from '../../../redux/slices/oldBuilder';
import {
  generateBucketDefInput,
  parseEventConditionToInput,
  toDatePickerInput,
} from './builderUtils';
import {
  DataTableQuerySettingKey,
  IBreakdownFieldInput,
  IBreakdownInput,
  ICohortExecuteInput,
  ICompareExpandEventInput,
  ICompareMergeEventInput,
  IDataTableExecuteInput,
  IEventConditionFieldsFragment,
  IFunnelExecuteInput,
  ILlmChatMessageFieldsFragment,
  ILlmChatMessageInput,
  IPathExecuteInput,
  IPathExpandEventInput,
  IPathFoldEventInput,
  IPathMergeEventInput,
  IPredictionExecuteInput,
  IQueryExecuteInput,
  IRetentionExecuteInput,
  MeasureType,
  PathDirectionKey,
  PathQuerySettingKey,
  QueryType,
  RetentionQuerySettingKey,
  TimeUnit,
} from '../../../utils/apollo/graphqlSchema';
import { partitionValuesToInput } from './partitionUtils';
import { MaybeNil } from '../../../utils/interfaces';
import { isEmptyEventCondition } from '../../../utils/builder';
import { IBuilderBreakdownGroup } from '../../../redux/slices/oldBuilder/breakdown/types';
import { isValidNumberWithinRange } from '../../../utils/numbers';
import {
  MAX_PREDICTION_TIME_WINDOW,
  MAX_TIME_WINDOW,
  MIN_PREDICTION_TIME_WINDOW,
  MIN_TIME_WINDOW,
} from '../../../config/constants';
import { assertCondition, isNotNil } from '../../../utils/types';
import isEmpty from 'lodash/isEmpty';
import { getBasicMeasureRowErrors } from '../../../utils/builder/validation';
import {
  parseCohortRowsToCohortRefInput,
  prepareCohortCompareInput,
  prepareCohortSampleCohortInput,
} from '../../../utils/builder/cohorts';
import { getCohortRowErrorsByQueryType } from './getCohortRowErrorsByQueryType';
import { prepareDataTableExecuteMeasureInput } from '../../../utils/builder/measures/inputs/dataTableInput';
import prepareFunnelExecuteInput from './funnel/prepareFunnelExecuteInput';
import validateFunnelBuilder from './funnel/validateFunnelBuilder';
import validateQueryBuilder from './query/validateQueryBuilder';
import prepareQueryExecuteInput from './query/prepareQueryExecuteInput';
import {
  convertPredicate,
  parseFilterToInput,
} from '../../../utils/filterFields';
import { countBuilderErrors } from './errorUtil';
import { checkIsForceBreakdownOverall } from '../Breakdown/BreakdownSettings/BreakdownOverall';
import { UnreachableCaseError } from 'ts-essentials';
import moment from 'moment';

const mapBreakdownGroupToInput = (
  group: IBuilderBreakdownGroup,
  activeSchemaId: string,
): IBreakdownFieldInput => ({
  field: group.field?.key ?? null,
  bucketDefInput: generateBucketDefInput(
    group.binning,
    group?.field?.key,
    activeSchemaId,
  ),
  predicate: group?.filter
    ? convertPredicate(group.filter, activeSchemaId)
    : null,
});

export const prepareBreakdownInput = (
  builderState: IBuilder,
): IBreakdownInput | null => {
  const { breakdown, breakdownCohorts } = builderState;

  const activeCohorts = breakdownCohorts.filter(
    (cohortRow) => cohortRow.isActive,
  );
  const groups = breakdown.groups.filter((group) => group.field);

  if (!activeCohorts?.length && !groups?.length) {
    return null;
  }

  const isForceBreakdownOverall = checkIsForceBreakdownOverall(builderState);

  const result: IBreakdownInput = {
    overall: isForceBreakdownOverall ? true : breakdown.overall,
    order: breakdown.order,
    cohortRefs: [],
    groupbys: null,
  };

  // check and parse segments
  if (activeCohorts?.length > 0) {
    result.cohortRefs = parseCohortRowsToCohortRefInput(
      activeCohorts,
      builderState.activeSchemaId,
    );
  }

  // check and parse groups
  if (groups?.length > 0) {
    result.groupbys = groups.map((group) =>
      mapBreakdownGroupToInput(group, builderState.activeSchemaId),
    );
  }

  return result;
};

const validateRetentionTimeWindow = (
  errors: IErrors,
  timeWindow: number | null,
  cohortUnit: TimeUnit,
) => {
  if (timeWindow === null) {
    if (cohortUnit === TimeUnit.AllTime) {
      errors.global = {
        timeWindow: true,
      };
    }

    return;
  }

  if (!isValidNumberWithinRange(timeWindow, 0, MAX_TIME_WINDOW)) {
    errors.global = {
      timeWindow: true,
    };
  }
};

export const validateTimeWindow = (errors: IErrors, timeWindow: number) => {
  if (!isValidNumberWithinRange(timeWindow, MIN_TIME_WINDOW, MAX_TIME_WINDOW)) {
    errors.global = {
      timeWindow: true,
    };
  }

  return;
};

type IValidatedInputResult<T> =
  | { isStateValid: false; errors: IErrors }
  | { isStateValid: true; input: T };

export const validateDataTableBuilderInput = ({
  state,
  replaceReport,
  analysisName,
  analysisDescription,
}: IBuilderInputPayload): IValidatedInputResult<IDataTableExecuteInput> => {
  const errors = {
    ...getCohortRowErrorsByQueryType(state, QueryType.Expression),
  };

  const dateInput = state.dateInput;
  // validate dataTableMeasures
  if (state.dataTableMeasures?.length > 0) {
    state.dataTableMeasures.forEach(
      (dataTableMeasure: IRow, dataTableMeasureIndex: number) => {
        // only need to run validation on Simple measures
        if (dataTableMeasure.type !== MeasureType.Compound) {
          validateQBMandatoryFields(
            dataTableMeasure,
            dataTableMeasureIndex,
            errors,
            RowType.dataTableMeasures,
          );
        }
      },
    );
  }

  const { measures, measuresProperties } = prepareDataTableExecuteMeasureInput(
    state.dataTableMeasures,
    state.activeSchemaId,
  );

  const { selectedOptions } = state;

  assertCondition(selectedOptions?.type === QueryType.DataTable);

  const input: IDataTableExecuteInput = {
    datePicker: toDatePickerInput(dateInput.datePicker),
    breakdown: prepareBreakdownInput(state),
    cohortRefs: parseCohortRowsToCohortRefInput(
      state[RowType.globalCohorts],
      state.activeSchemaId,
    ),
    dateKind: selectedOptions.dateKind,
    filter: state.filter
      ? parseFilterToInput(state.filter, state.activeSchemaId)
      : null,
    measures,
    measuresProperties,
    unit: TimeUnit.AllTime,
    schemaId: state.activeSchemaId,
    sourceId: state.sourceId ?? null,
    timeWindow: null,
    samplePercentage: null,
    settings: state.additionalSettings as DataTableQuerySettingKey[],
    display: replaceReport && analysisName ? analysisName : null,
    description:
      replaceReport && analysisDescription ? analysisDescription : null,
    comparisonConfig: null,
    llmChatMessages: state.llmContext.previousMessages.map(
      llmChatMessageToInput,
    ),
  };

  // handle comparison config
  if (state.comparisonConfig) {
    const { offset, timeUnit } = state.comparisonConfig;

    input.comparisonConfig = {
      offset,
      timeUnit,
    };
  }

  if (
    state.additionalSettings.includes(DataTableQuerySettingKey.Sampling) &&
    state.formulaSamplingRate !== null
  ) {
    input.samplePercentage = state.formulaSamplingRate;
  }

  if (areThereAnyErrors(errors)) {
    return { isStateValid: false, errors };
  }

  return { isStateValid: true, input };
};

const validatePathMandatoryFields = (
  conditionsMatrix: IEventConditionFieldsFragment[][],
  errors: IErrors,
  type: keyof IErrors,
) => {
  errors[type] = {};
  conditionsMatrix.forEach((path, index) => {
    path.forEach((eventCondition, eventIndex) => {
      if (isEmptyEventCondition(eventCondition)) {
        errors[type][index] = {
          ...errors[type][index],
          [eventIndex]: true,
        };
      }
    });
  });
  return;
};

const validateMergeEvent = (
  mergeEvent: IBuilderMergeEvent,
  errors: IErrors,
  type: keyof IErrors,
  index: number,
) => {
  mergeEvent.eventConditions.forEach((eventCondition, eventIndex) => {
    if (isEmptyEventCondition(eventCondition)) {
      errors[type][index] = {
        ...errors[type][index],
        [eventIndex]: true,
      };
    }
  });
};

function validateExpandEvent(
  expandEvent: IBuilderExpandEvent,
  errors: IErrors,
  type: keyof IErrors,
  index: number,
) {
  if (isEmptyEventCondition(expandEvent.eventCondition) || !expandEvent.field) {
    errors[type][index] = true;
  }
}

function validateFoldEvent(
  foldEvent: IBuilderFoldEvent,
  errors: IErrors,
  type: keyof IErrors,
  index: number,
) {
  if (isEmptyEventCondition(foldEvent.eventCondition)) {
    errors[type][index] = true;
  }
}

export const validateMergeEvents = (
  mergeEvents: IBuilderMergeEvent[],
  errors: IErrors,
  type: keyof IErrors,
) => {
  errors[type] = {};
  mergeEvents
    .filter((mergeEvent) => mergeEvent.isActive)
    .forEach((mergeEvent, index: number) => {
      validateMergeEvent(mergeEvent, errors, type, index);
    });
};

export const validateExpandEvents = (
  expandEvents: IBuilderExpandEvent[],
  errors: IErrors,
  type: keyof IErrors,
) => {
  errors[type] = {};
  expandEvents
    .filter((expandEvent) => expandEvent.isActive)
    .forEach((expandEvent, index: number) => {
      validateExpandEvent(expandEvent, errors, type, index);
    });
};

const validateFoldEvents = (
  foldEvents: IBuilderFoldEvent[],
  errors: IErrors,
  type: keyof IErrors,
) => {
  errors[type] = {};
  foldEvents
    .filter((foldEvent) => foldEvent.isActive)
    .forEach((foldEvent, index) => {
      validateFoldEvent(foldEvent, errors, type, index);
    });
};

export const parseMergeEvents = (
  mergeEvents: IBuilderMergeEvent[],
  schemaId: string,
): (IPathMergeEventInput & ICompareMergeEventInput)[] => {
  return mergeEvents
    .filter((mergeEvent) => mergeEvent.isActive)
    .map((mergeEvent) => {
      return {
        name:
          isNil(mergeEvent.name) || mergeEvent.isNameAutoGenerated
            ? null
            : mergeEvent.name,
        eventConditions:
          mergeEvent.eventConditions?.map((eventCondition) => {
            return parseEventConditionToInput(eventCondition, schemaId);
          }) ?? [],
      };
    });
};

export const parseExpandEvents = (
  expandEvents: IBuilderExpandEvent[],
  schemaId: string,
): (IPathExpandEventInput & ICompareExpandEventInput)[] => {
  return expandEvents
    .filter((expandEvent) => expandEvent.isActive)
    .map((expandEvent) => {
      const { eventCondition, field } = expandEvent;
      if (isNil(eventCondition) || isNil(field)) {
        return null;
      }
      return {
        field: field.key,
        eventCondition: parseEventConditionToInput(eventCondition, schemaId),
      };
    })
    .filter(isNotNil);
};

const parseFoldEvents = (
  foldEvents: IBuilderFoldEvent[],
  schemaId: string,
): IPathFoldEventInput[] => {
  return foldEvents
    .filter((foldEvent) => foldEvent.isActive)
    .map((foldEvent) => {
      const { eventCondition } = foldEvent;
      if (isNil(eventCondition)) {
        return null;
      }
      return {
        eventCondition: parseEventConditionToInput(eventCondition, schemaId),
      };
    })
    .filter(isNotNil);
};

export const areThereAnyErrors = (
  errors: MaybeNil<IErrors>,
): errors is IErrors => countBuilderErrors(errors) > 0;

export const validatePathBuilderInput = ({
  state,
  replaceReport,
  analysisName,
  analysisDescription,
}: IBuilderInputPayload): IValidatedInputResult<IPathExecuteInput> => {
  const errors = {
    ...getCohortRowErrorsByQueryType(state, QueryType.Path),
  };

  const { selectedOptions, dateInput } = state;

  assertCondition(selectedOptions?.type === QueryType.Path);

  validatePathMandatoryFields(state.paths, errors, RowType.paths);
  validateMergeEvents(state.mergeEvents, errors, RowType.mergeEvents);
  validateExpandEvents(state.expandEvents, errors, RowType.expandEvents);
  validateFoldEvents(state.foldEvents, errors, RowType.foldEvents);

  const input: IPathExecuteInput = {
    datePicker: toDatePickerInput(dateInput.datePicker),
    direction:
      state.direction === PathDirectionKey.Backward
        ? PathDirectionKey.Backward
        : PathDirectionKey.Forward,
    startConditions: state.paths[0].map((eventCondition: any) => {
      return parseEventConditionToInput(eventCondition, state.activeSchemaId);
    }),
    endConditions: state.paths[1].map((eventCondition: any) => {
      return parseEventConditionToInput(eventCondition, state.activeSchemaId);
    }),
    filter: state.filter
      ? parseFilterToInput(state.filter, state.activeSchemaId)
      : null,
    schemaId: state.activeSchemaId,
    partitions: partitionValuesToInput(selectedOptions.partitionValues),
    subject: selectedOptions.subject ?? null,
    groupby: selectedOptions.groupby,
    cohortRefs: parseCohortRowsToCohortRefInput(
      state[RowType.globalCohorts],
      state.activeSchemaId,
    ),
    mergeEvents: parseMergeEvents(state.mergeEvents, state.activeSchemaId),
    expandEvents: parseExpandEvents(state.expandEvents, state.activeSchemaId),
    foldEvents: parseFoldEvents(state.foldEvents, state.activeSchemaId),
    sourceId: state.sourceId ?? null,
    breakdown: prepareBreakdownInput(state),
    samplePercentage: null,
    mode: selectedOptions.mode,
    settings: state.additionalSettings as PathQuerySettingKey[],
    display: replaceReport && analysisName ? analysisName : null,
    description:
      replaceReport && analysisDescription ? analysisDescription : null,
    llmChatMessages: state.llmContext.previousMessages.map(
      llmChatMessageToInput,
    ),
  };

  if (
    state.additionalSettings.includes(PathQuerySettingKey.Sampling) &&
    state.formulaSamplingRate !== null
  ) {
    input.samplePercentage = state.formulaSamplingRate;
  }

  if (areThereAnyErrors(errors)) {
    return { isStateValid: false, errors };
  }

  return {
    input,
    isStateValid: true,
  };
};

export const validateRetentionBuilderInput = ({
  state,
  replaceReport,
  analysisName,
  analysisDescription,
}: IBuilderInputPayload): IValidatedInputResult<IRetentionExecuteInput> => {
  const errors = {
    ...getCohortRowErrorsByQueryType(state, QueryType.Retention),
  };

  validatePathMandatoryFields(
    state.retention.retentionConditions,
    errors,
    RowType.retention,
  );
  const { selectedOptions, dateInput } = state;

  assertCondition(selectedOptions?.type === QueryType.Retention);

  const input: IRetentionExecuteInput = {
    datePicker: toDatePickerInput(dateInput.datePicker),
    schemaId: state.activeSchemaId,
    cohortUnit: selectedOptions.cohortUnit,
    retentionUnit: state.retention.activeRetentionUnit,
    timeWindow: state.timeWindow ?? null,
    startConditions: state.retention.retentionConditions[0].map(
      (eventCondition) =>
        parseEventConditionToInput(eventCondition, state.activeSchemaId),
    ),
    returnConditions: state.retention.retentionConditions[1].map(
      (eventCondition) =>
        parseEventConditionToInput(eventCondition, state.activeSchemaId),
    ),
    filter: state.filter
      ? parseFilterToInput(state.filter, state.activeSchemaId)
      : null,
    subject: selectedOptions.subject ?? null,
    attribution: selectedOptions.attribution,
    groupby: selectedOptions.groupby,
    calculation: selectedOptions.calculation,
    calculationWindow: selectedOptions.calculationWindow,
    customTimeBrackets: state.retention.customTimeBrackets,
    retentionView: selectedOptions.retentionView,
    sourceId: state.sourceId ?? null,
    samplePercentage: null,
    breakdown: prepareBreakdownInput(state),
    cohortRefs: parseCohortRowsToCohortRefInput(
      state[RowType.globalCohorts],
      state.activeSchemaId,
    ),
    settings: state.additionalSettings as RetentionQuerySettingKey[],
    display: replaceReport && analysisName ? analysisName : null,
    description:
      replaceReport && analysisDescription ? analysisDescription : null,
    llmChatMessages: state.llmContext.previousMessages.map(
      llmChatMessageToInput,
    ),
  };

  if (
    state.additionalSettings.includes(RetentionQuerySettingKey.Sampling) &&
    state.formulaSamplingRate !== null
  ) {
    input.samplePercentage = state.formulaSamplingRate;
  }

  if (errors.retention && Object.keys(errors.retention).length === 0) {
    delete errors.retention;
  }
  validateRetentionTimeWindow(errors, input.timeWindow, input.cohortUnit);

  if (areThereAnyErrors(errors)) {
    return { isStateValid: false, errors };
  }

  return {
    isStateValid: true,
    input,
  };
};

export const llmChatMessageToInput = (
  chatMessage: ILlmChatMessageFieldsFragment,
): ILlmChatMessageInput => {
  return {
    sender: chatMessage.sender,
    text: chatMessage.text,
    createdAtUnixTimestamp: moment(chatMessage.createdAt).unix(),
  };
};

export const validateCohortExecuteInput = ({
  state,
  queryType,
}: IBuilderInputPayload): IValidatedInputResult<ICohortExecuteInput> => {
  const errors = getCohortRowErrorsByQueryType(state, queryType);

  // Check errors and finish early
  if (areThereAnyErrors(errors)) {
    return {
      errors,
      isStateValid: false,
    };
  }

  const cohortRows = state[RowType.builderCohorts];
  const builderSampledCohort = state.builderSampledCohort;
  const { sampleOperation, samplingRate } = builderSampledCohort;
  const dateInput = state.dateInput;

  let samplePercentage = null;
  let sampleAbsolute = null;

  if (
    samplingRate.type === CohortSampleContextType.Percentage &&
    samplingRate.samplePercentage
  ) {
    samplePercentage = samplingRate.samplePercentage;
  } else if (
    samplingRate.type === CohortSampleContextType.Absolute &&
    samplingRate.sampleAbsolute
  ) {
    sampleAbsolute = samplingRate.sampleAbsolute;
  }

  const input: ICohortExecuteInput = {
    schemaId: state.activeSchemaId,
    datePicker: toDatePickerInput(dateInput.datePicker),
    sourceId: state.sourceId ?? null,
    operation: sampleOperation?.key ?? null,
    samplePercentage,
    sampleAbsolute,
    cohort: prepareCohortSampleCohortInput(
      cohortRows,
      builderSampledCohort,
      state.activeSchemaId,
    ),
    compareInput: prepareCohortCompareInput(
      sampleOperation?.key,
      state[RowType.expandEvents],
      state[RowType.mergeEvents],
      state.activeSchemaId,
    ),
    display: null,
    description: null,
    llmChatMessages: state.llmContext.previousMessages.map(
      llmChatMessageToInput,
    ),
  };

  return {
    input,
    isStateValid: true,
  };
};

const getPredictionBuilderErrors = (state: IBuilder) => {
  const errors = {
    // validate cohorts
    ...getCohortRowErrorsByQueryType(state, QueryType.Prediction),
  };

  // validate other builder fields
  const prediction = state.prediction;

  if (
    !isNaN(prediction.timeWindow) &&
    !isValidNumberWithinRange(
      prediction.timeWindow,
      MIN_PREDICTION_TIME_WINDOW,
      MAX_PREDICTION_TIME_WINDOW,
    )
  ) {
    errors.prediction = {
      ...(errors?.prediction ?? {}),
      timeWindow: true,
    };
  }

  if (isEmptyEventCondition(prediction.eventCondition)) {
    errors.prediction = {
      ...(errors?.prediction ?? {}),
      events: true,
    };
  }

  return errors;
};

export const validatePredictionBuilderInput = ({
  state,
  replaceReport,
  analysisName,
  analysisDescription,
}: IBuilderInputPayload): IValidatedInputResult<IPredictionExecuteInput> => {
  // Validate input
  const errors = getPredictionBuilderErrors(state);

  // If any error occurred - escape early
  if (areThereAnyErrors(errors)) {
    return {
      isStateValid: false,
      errors,
    };
  }

  const eventCondition = state.prediction.eventCondition;
  const dateInput = state.dateInput;
  const cohorts = parseCohortRowsToCohortRefInput(
    state[RowType.predictionCohorts],
    state.activeSchemaId,
  );

  const input: IPredictionExecuteInput = {
    datePicker: toDatePickerInput(dateInput.datePicker),
    window: state.prediction.timeWindow,
    schemaId: state.activeSchemaId,
    eventCondition: parseEventConditionToInput(
      eventCondition,
      state.activeSchemaId,
    ),
    sourceId: state.sourceId ?? null,
    sourceCohortRef: cohorts[0],
    targetCohortRef: cohorts[1],
    samplePercentage: null,
    display: replaceReport && analysisName ? analysisName : null,
    description:
      replaceReport && analysisDescription ? analysisDescription : null,
  };

  return { input, isStateValid: true };
};

const validateQBMandatoryFields = (
  row: IRow,
  rowIndex: number,
  errors: IErrors,
  rowType: RowType,
) => {
  const basicMeasureErrors = getBasicMeasureRowErrors(row);

  if (!isEmpty(basicMeasureErrors)) {
    errors[rowType] = {
      ...(errors?.[rowType] ?? {}),
      [rowIndex]: {
        ...(errors?.[rowType]?.[rowIndex] ?? {}),
        ...basicMeasureErrors,
      },
    };
  }
};

export const validateEventConditions = (
  eventConditions: IEventConditionFieldsFragment[],
) =>
  eventConditions.every(
    (eventCondition) => !isEmptyEventCondition(eventCondition),
  );

export const validateMergeEventsConditions = (
  mergeEvents: MaybeNil<IBuilderMergeEvent[]>,
) => {
  return (mergeEvents || []).every(
    (mergeEvent) =>
      !mergeEvent.isActive ||
      validateEventConditions(mergeEvent.eventConditions),
  );
};

export const validateExpandEventConditions = (
  expandEvents: MaybeNil<IBuilderExpandEvent[]>,
) => {
  return (expandEvents || []).every(
    (expandEvent) =>
      !expandEvent.isActive ||
      !isEmptyEventCondition(expandEvent.eventCondition),
  );
};

export const validateFoldEventConditions = (
  foldEvents: MaybeNil<IBuilderFoldEvent[]>,
) => {
  return (foldEvents || []).every(
    (foldEvent) =>
      !foldEvent.isActive || !isEmptyEventCondition(foldEvent.eventCondition),
  );
};

type IGenerateExecutionInput =
  | IValidatedInputResult<IQueryExecuteInput>
  | IValidatedInputResult<IFunnelExecuteInput>
  | IValidatedInputResult<IPathExecuteInput>
  | IValidatedInputResult<IRetentionExecuteInput>
  | IValidatedInputResult<ICohortExecuteInput>
  | IValidatedInputResult<IPredictionExecuteInput>
  | IValidatedInputResult<IDataTableExecuteInput>;

export const generateExecutionInput = (
  args: IBuilderInputPayload,
): IGenerateExecutionInput => {
  const queryType = args.queryType;

  // Significance has its own execution and validation logic
  assertCondition(queryType !== QueryType.Significance);

  switch (queryType) {
    case QueryType.Expression: {
      const { state } = args;

      const errors = validateQueryBuilder(state);

      if (areThereAnyErrors(errors)) {
        return {
          errors,
          isStateValid: false,
        };
      }

      const input = prepareQueryExecuteInput(args);

      return {
        input,
        isStateValid: true,
      };
    }
    case QueryType.Funnel: {
      const { state } = args;

      const errors = validateFunnelBuilder(state);

      if (areThereAnyErrors(errors)) {
        return {
          errors,
          isStateValid: false,
        };
      }

      const input = prepareFunnelExecuteInput(args);

      return {
        input,
        isStateValid: true,
      };
    }
    case QueryType.Retention:
      return validateRetentionBuilderInput(args);
    case QueryType.Prediction:
      return validatePredictionBuilderInput(args);
    case QueryType.Path:
      return validatePathBuilderInput(args);
    case QueryType.CohortSample:
      return validateCohortExecuteInput(args);
    case QueryType.DataTable:
      return validateDataTableBuilderInput(args);
    default:
      throw new UnreachableCaseError(queryType);
  }
};
